import { NOTIFICATIONS_LIST_ID } from "testIds";
import styled, { useTheme } from "styled-components";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";
import NotificationItem from "features/notifications/NotificationItem";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Empty from "common/Empty/Empty";
import Text from "common/Typography/Text";
import React, { useMemo, useState } from "react";
import useNotificationsList from "@shared/features/notifications/useNotificationsList";
import {
  LanguageNameSpaces,
  Notification,
  NotificationScreens,
  NotificationShareTradeData,
} from "@shared/types";
import useNotificationsBanners from "@shared/features/notifications/useNotificationsBanners";
import { sharedLib } from "@shared/services/shared.service";
import FlatList from "common/Lists/FlatList.tsx";
import ShareImageModal from "Global/ShareImageModal.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

interface NotificationsListProps {
  closeModal: () => void;
}

const NotificationsList = ({ closeModal }: NotificationsListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isNewLook } = useTheme();
  const { data: notifications, isFetching } =
    sharedLib.sharedApi.notifications.endpoints.getNotifications.useQueryState(
      undefined
    );
  const { sortedNotificationsArray, numNotifications } =
    useNotificationsList(notifications);
  const tradesBanners = useNotificationsBanners(sortedNotificationsArray);
  const [shareTradeData, setShareTradeData] = useState<
    NotificationShareTradeData | undefined
  >();

  const firstUpgradeNotificationIndex = useMemo(
    () =>
      sortedNotificationsArray?.findIndex((notifications) => {
        return notifications[0].screen === NotificationScreens.UPGRADE_PACKAGE;
      }),
    [sortedNotificationsArray]
  );

  const renderContent = ({
    item,
    index,
  }: {
    item: Notification[];
    index: number;
  }) => {
    return (
      <NotificationItem
        key={index}
        notification={item}
        showUpgrade={index === firstUpgradeNotificationIndex}
        closeModal={closeModal}
        bannerImageLink={tradesBanners?.[item?.[0]?.component_id]}
        setShareTradeData={setShareTradeData}
      />
    );
  };

  const list = (
    <div style={{ height: LIST_HEIGHT }}>
      <FlatList
        data={sortedNotificationsArray || []}
        loading={isFetching}
        renderContent={renderContent}
        refreshing={isFetching}
        gap={0}
        wrapperStyle={{ padding: 0 }}
      />
    </div>
  );

  return (
    <Wrapper data-testid={NOTIFICATIONS_LIST_ID}>
      <LoadingSpinnerOverlay
        isLoading={isFetching}
        spinnerMarginTop={50}
        hideContentWhileLoading={true}
      >
        {notifications &&
          (numNotifications === 0 ? (
            isNewLook ? (
              <PanelEmpty
                title={t("empty.no_notifications")}
                style={NEW_LOOK_EMPTY_STYLE}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ marginTop: 30 }}
                description={
                  <Text type="secondary">{t("empty.no_notifications")}</Text>
                }
              />
            )
          ) : (
            list
          ))}
        <ShareImageModal
          visible={!!shareTradeData}
          closeModal={() => setShareTradeData(undefined)}
          imageUrl={shareTradeData?.imageUrl}
          imageTitle={shareTradeData?.imageTitle}
        />
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default NotificationsList;

const LIST_HEIGHT = 400;
const NEW_LOOK_EMPTY_STYLE = { marginTop: 96 };

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: ${LIST_HEIGHT}px;
  max-height: calc(100dvh - 180px);
  overflow: auto;
  position: relative;
`;
